/**
 * Programmatic ringtone using Web Audio API.
 * Unlocks on first user gesture so incoming calls can ring even when
 * the AudioContext would otherwise stay suspended by autoplay policy.
 */

let ctx = null;
let gainNode = null;
let ringInterval = null;
let isPlaying = false;
let unlockBound = false;

function getAudioContext() {
  if (!ctx || ctx.state === 'closed') {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return ctx;
}

/** Resume AudioContext after a user gesture (required by browsers). */
export async function unlockAudio() {
  try {
    const audioCtx = getAudioContext();
    if (audioCtx.state === 'suspended') {
      await audioCtx.resume();
    }
    return audioCtx.state === 'running';
  } catch (_) {
    return false;
  }
}

/** Bind once: any click/key/touch on the page unlocks audio for later rings. */
export function bindAudioUnlock() {
  if (unlockBound || typeof window === 'undefined') return () => {};
  unlockBound = true;
  const unlock = () => {
    unlockAudio();
  };
  const opts = { capture: true, passive: true };
  window.addEventListener('pointerdown', unlock, opts);
  window.addEventListener('keydown', unlock, opts);
  window.addEventListener('touchstart', unlock, opts);
  return () => {
    window.removeEventListener('pointerdown', unlock, opts);
    window.removeEventListener('keydown', unlock, opts);
    window.removeEventListener('touchstart', unlock, opts);
    unlockBound = false;
  };
}

function playTone(audioCtx, gain, freq, startTime, duration) {
  const osc = audioCtx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startTime);

  const env = audioCtx.createGain();
  env.gain.setValueAtTime(0, startTime);
  env.gain.linearRampToValueAtTime(0.35, startTime + 0.02);
  env.gain.setValueAtTime(0.35, startTime + duration - 0.02);
  env.gain.linearRampToValueAtTime(0, startTime + duration);

  osc.connect(env);
  env.connect(gain);
  osc.start(startTime);
  osc.stop(startTime + duration);
}

function ringOnce() {
  try {
    const audioCtx = getAudioContext();
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }
    if (!gainNode || gainNode.context !== audioCtx) {
      gainNode = audioCtx.createGain();
      gainNode.gain.value = 0.3;
      gainNode.connect(audioCtx.destination);
    }
    const now = audioCtx.currentTime;
    // Two short bursts — classic dual-tone ring
    playTone(audioCtx, gainNode, 440, now, 0.4);
    playTone(audioCtx, gainNode, 480, now, 0.4);
    playTone(audioCtx, gainNode, 440, now + 0.5, 0.4);
    playTone(audioCtx, gainNode, 480, now + 0.5, 0.4);
  } catch (_) {
    /* Web Audio unavailable — silent fallback */
  }
}

export function startRingtone() {
  if (isPlaying) return;
  isPlaying = true;
  unlockAudio().finally(() => {
    if (!isPlaying) return;
    ringOnce();
  });
  ringInterval = setInterval(() => {
    if (!isPlaying) return;
    ringOnce();
  }, 2400);
}

export function stopRingtone() {
  isPlaying = false;
  if (ringInterval) {
    clearInterval(ringInterval);
    ringInterval = null;
  }
}
